import { Injectable, inject } from '@angular/core'

import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import {
  Observable,
  Subject,
  catchError,
  finalize,
  merge,
  take,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs'

import { ConversationApiService } from '@flowx/designer/modules/ai/common/api/conversation-api.service'
import { StreamMessageRequest } from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import { ActionProgressEvent } from '@flowx/designer/modules/ai/common/api/conversation-stream.types'
import { ConversationSelectors } from '@flowx/designer/modules/ai/conversation/conversation.selectors'
import {
  selectAppId,
  selectWorkspaceId,
} from '@flowx/designer/shared/store/selectors/router.selectors'

import { ConversationActions } from './conversation.actions'

import { AiConversationServiceActions } from '../store/ai.actions'

@Injectable({
  providedIn: 'root',
})
export class AiConversationStreamService {
  #store = inject(Store)
  #conversationApiService = inject(ConversationApiService)
  #destroy$ = new Subject<void>()
  #actions$ = inject(Actions)

  static getProgressContent(progressEvent: ActionProgressEvent): string | null {
    if (!progressEvent) {
      return null
    }

    switch (progressEvent.progressType) {
      case 'MAIN_OUTCOME':
        return progressEvent.main?.content || null
      case 'CHAT':
        return progressEvent.chat?.content || null
      case 'INTERRUPT':
        return progressEvent.interrupt?.message?.message || null
      case 'LOGGING':
        return progressEvent.logging?.logMessage || null
      case 'REASONING':
        return progressEvent.reasoning?.reasoning || null
      case 'ERROR':
        return progressEvent.error?.detail || progressEvent.error?.title || null
      default:
        return null
    }
  }

  cleanup(): void {
    this.#destroy$.next()
  }

  listenForMessageUpdates(payload: StreamMessageRequest): Observable<ActionProgressEvent> {
    this.#store.dispatch(
      ConversationActions.conversationStreamingStarted({
        messageId: payload.messageId,
      })
    )

    let hasStreamError = false
    const workspaceId = this.#store.selectSignal(selectWorkspaceId)()
    const appId = this.#store.selectSignal(selectAppId)()

    return this.#conversationApiService.streamMessage(payload, workspaceId, appId).pipe(
      takeUntil(this.#destroy$),
      takeWhile(() => {
        const activeNonDefaultThreadId = this.#store.selectSignal(
          ConversationSelectors.activeNonDefaultThreadId
        )()
        return activeNonDefaultThreadId === payload.threadId
      }),
      takeUntil(
        merge(
          this.#actions$.pipe(ofType(ConversationActions.discardOutcomeClick)),
          this.#actions$.pipe(ofType(ConversationActions.discardThreadClick)),
          this.#actions$.pipe(ofType(ConversationActions.defaultThreadMessageCancelled))
        ).pipe(
          take(1),
          tap(() =>
            this.#store.dispatch(
              AiConversationServiceActions.conversationMessageStreamEnded({
                messageId: payload.messageId,
                conversationId: payload.conversationId,
                threadId: payload.threadId,
              })
            )
          )
        )
      ),
      tap((messageUpdates) => {
        this.#store.dispatch(
          AiConversationServiceActions.aiConversationStreamUpdate({
            updates: messageUpdates,
          })
        )
      }),
      catchError((error) => {
        hasStreamError = true
        this.#store.dispatch(
          AiConversationServiceActions.conversationMessageStreamError({
            messageId: payload.messageId,
            threadId: payload.threadId,
            conversationId: payload.conversationId,
          })
        )
        throw error
      }),
      finalize(() => {
        this.#store.dispatch(
          AiConversationServiceActions.conversationMessageStreamEnded({
            messageId: payload.messageId,
            conversationId: payload.conversationId,
            threadId: payload.threadId,
          })
        )

        if (!hasStreamError) {
          this.#store.dispatch(
            AiConversationServiceActions.conversationMessageStreamSuccess({
              messageId: payload.messageId,
              conversationId: payload.conversationId,
              threadId: payload.threadId,
            })
          )
        }
      })
    )
  }
}
